import { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { createRefetchContainer, graphql } from 'react-relay';
import serverVars from 'server-vars';
import { CUSTOMER_TYPE_TRADE } from '../../constants/userConstants';
import { sbRespRefineMenuFiltersMap } from '../SbRespRefineMenu/SbRespRefineMenuFilters/sbRespRefineMenuFiltersMap';
import { hasPersonalizedFilterQueryParam } from '../../shared/helpers/personalizedFiltersHelpers';
import { getLocalHistoryItemIds } from 'dibs-recent-history/exports/getLocalHistoryItemIds';
import { MAX_CAROUSEL_RECENTLY_VIEWED_ITEMS } from 'dibs-recent-history/exports/constants';

/* Components */
import { SharedUserAndRelayVarLoader } from '../../shared/SharedUserAndRelayVarLoader';
import { SbSharedRefetchContainer } from '../SbSharedRefetchContainer/SbSharedRefetchContainer';
import { SbRespComponents } from '../SbRespComponents/SbRespComponents';
import { SbSharedHead } from '../SbSharedHead/SbSharedHead';
import { SbSharedTrackingContainer } from '../SbSharedTrackingContainer/SbSharedTrackingContainer';
import { CurrencyProvider } from 'dibs-buyer-layout/exports/CurrencyProvider';
import { ServerVarsContext } from '../../global/ServerVarsContext/ServerVarsContext';
import { SearchCorrectionsProvider } from '../useSearchCorrections';

/* Styles */
import './SbRespLayout.scss'; // temp fix for FINDING-13444, will eventually change base style in DBL

class SbRespLayoutComponent extends Component {
    constructor() {
        super();

        this.handleSbRefetch = this.handleSbRefetch.bind(this);
        this.handleUserRefetch = this.handleUserRefetch.bind(this);

        this.relayVars = {};
    }

    handleUserRefetch(variables, ...args) {
        /**
         * Need to refetch sb items for trade users, the price depends on the `isTrade` variable
         * being set.
         *
         * Only do this after login, hence the server vars check, subsequent visits will depend on
         * the ut query param.
         */
        const { isClient } = variables || {};
        const hasPersonalizedFilter = isClient && hasPersonalizedFilterQueryParam();
        const fetchSb = variables.isTrade && serverVars.get('userType') !== CUSTOMER_TYPE_TRADE;
        const { viewer, relayVariables } = this.props;
        const { displayUriRef } = (viewer && viewer.itemSearch) || {};
        const { followSearchPages } = relayVariables || {};
        const localRecentHistoryItemIds = getLocalHistoryItemIds({ isClient });
        this.doRefetch(
            {
                ...variables,
                fetchSb,
                skipUser: false,
                hasPersonalizedFilter,
                followSearchPages: displayUriRef ? [displayUriRef] : followSearchPages,
                localRecentHistoryItemIds,
                fetchUserRecentHistoryItem:
                    localRecentHistoryItemIds.length < MAX_CAROUSEL_RECENTLY_VIEWED_ITEMS,
            },
            ...args
        );
    }

    handleSbRefetch(variables, renderVariables, callback) {
        this.doRefetch({ ...variables, fetchSb: true }, renderVariables, () => {
            callback();

            this.props.runNotificationQueue();
        });
    }

    doRefetch(variables, renderVariables, callback) {
        this.relayVars = { ...this.relayVars, ...variables };
        this.props.relay.refetch(
            this.relayVars,
            { ...this.relayVars, ...renderVariables, fetchSb: true },
            callback
        );
    }

    render() {
        const { viewer, localeDomain, reinitTracking } = this.props;
        const user = viewer.user || null;
        const { itemSearch, regionalInfo = null, shippingFilters = null } = viewer;

        return (
            <Fragment>
                <SharedUserAndRelayVarLoader onUserRefetch={this.handleUserRefetch} />
                <SearchCorrectionsProvider itemSearch={itemSearch}>
                    {({ searchCorrectionTerm }) => (
                        <CurrencyProvider
                            render={({ currency }) => (
                                <Fragment>
                                    <SbSharedTrackingContainer
                                        user={user}
                                        currency={currency}
                                        itemSearch={itemSearch}
                                        reinitTracking={reinitTracking}
                                        refineMenuFiltersMap={sbRespRefineMenuFiltersMap}
                                        searchCorrectionTerm={searchCorrectionTerm}
                                    />
                                    <SbSharedHead
                                        itemSearch={itemSearch}
                                        localeDomain={localeDomain}
                                    />

                                    <SbSharedRefetchContainer
                                        viewer={viewer}
                                        itemSearch={itemSearch}
                                        regionalInfo={regionalInfo}
                                        shippingFilters={shippingFilters}
                                        onSbRefetch={this.handleSbRefetch}
                                        shouldShowSpinner={false}
                                    />
                                    <SbRespComponents
                                        viewer={viewer}
                                        itemSearch={itemSearch}
                                        user={user}
                                        currency={currency}
                                        isClient={!!this.relayVars.isClient}
                                        shippingFilters={shippingFilters}
                                    />
                                </Fragment>
                            )}
                        />
                    )}
                </SearchCorrectionsProvider>
            </Fragment>
        );
    }
}

SbRespLayoutComponent.propTypes = {
    viewer: PropTypes.object,
    relay: PropTypes.object.isRequired,
    localeDomain: PropTypes.string.isRequired,
    runNotificationQueue: PropTypes.func.isRequired,
    reinitTracking: PropTypes.func,
    relayVariables: PropTypes.shape({
        followSearchPages: PropTypes.arrayOf(PropTypes.string),
    }).isRequired,
};

SbRespLayoutComponent.defaultProps = {
    runNotificationQueue: () => {},
};

SbRespLayoutComponent.contextType = ServerVarsContext;

const mapStateToProps = state => {
    const { intlVariables, relayVariables } = state;

    return {
        localeDomain: intlVariables.localeDomain,
        relayVariables: relayVariables.variables,
    };
};

export const SbRespLayout = createRefetchContainer(
    connect(mapStateToProps)(SbRespLayoutComponent),
    {
        viewer: graphql`
            fragment SbRespLayout_viewer on Viewer
            @argumentDefinitions(
                hasPersonalizedFilter: { type: "Boolean", defaultValue: false }
                userZipCode: { type: "String", defaultValue: "" }
                userCountryCode: { type: "String", defaultValue: "" }
                fetchRegionalInfo: { type: "Boolean", defaultValue: false }
            ) {
                ...SbRespComponents_viewer
                itemSearch(
                    uriRef: $uriRef
                    first: $first
                    page: $page
                    localeOrigin: $localeOrigin
                    eligibleAbTest: $eligibleAbTest
                    regions: $regions
                    disableForceFacet: $disableForceFacet
                ) {
                    pageType
                    displayUriRef
                    # Used by SearchCorrectionsProvider
                    ...useSearchCorrections_itemSearch
                    ...SbSharedRefetchContainer_itemSearch
                    ...SbSharedTrackingContainer_itemSearch
                    ...SbSharedHead_itemSearch
                    ...SbRespComponents_itemSearch @include(if: $fetchSb)
                }
                user(userId: $userId) @include(if: $fetchUser) {
                    ...SbSharedTrackingContainer_user
                    ...SbRespComponents_user
                }
                regionalInfo(zipCode: $userZipCode, countryCode: $userCountryCode)
                @include(if: $fetchRegionalInfo) {
                    ...SbSharedRefetchContainer_regionalInfo
                }
                shippingFilters(
                    uriRef: $uriRef
                    zipCode: $userZipCode
                    countryCode: $userCountryCode
                ) @include(if: $hasPersonalizedFilter) {
                    ...SbSharedRefetchContainer_shippingFilters
                    ...SbRespComponents_shippingFilters
                }
            }
        `,
    },
    graphql`
        query SbRespLayoutRefetchQuery(
            $userId: String = ""
            $fetchUser: Boolean!
            $fetchSb: Boolean!
            $skipUser: Boolean = true
            $isTrade: Boolean!
            $uriRef: String
            $first: Int!
            $page: Int!
            $localeOrigin: String = ""
            $showSeller: Boolean!
            $followSearchPages: [String]
            $followSearchTypes: [types] = [SEARCH, USER_PREFERENCE]
            $contentModuleId: String = ""
            $eligibleAbTest: String = ""
            $regions: String = ""
            $hasPersonalizedFilter: Boolean!
            $userZipCode: String = ""
            $userCountryCode: String = ""
            $fetchRegionalInfo: Boolean = false
            $disableForceFacet: Boolean = true
            $isClient: Boolean!
            $localRecentHistoryItemIds: [String] = []
            $fetchUserRecentHistoryItem: Boolean = false
        ) {
            viewer {
                ...SbRespComponents_viewer
                itemSearch(
                    uriRef: $uriRef
                    first: $first
                    page: $page
                    localeOrigin: $localeOrigin
                    eligibleAbTest: $eligibleAbTest
                    regions: $regions
                    disableForceFacet: $disableForceFacet
                ) @include(if: $fetchSb) {
                    pageType
                    displayUriRef
                    ...SbSharedRefetchContainer_itemSearch
                    ...SbRespComponents_itemSearch
                    ...SbSharedHead_itemSearch
                    ...SbSharedTrackingContainer_itemSearch
                }
                user(userId: $userId) @include(if: $fetchUser) @skip(if: $skipUser) {
                    ...SbSharedTrackingContainer_user
                    ...SbRespComponents_user
                }
                regionalInfo(zipCode: $userZipCode, countryCode: $userCountryCode)
                @include(if: $fetchRegionalInfo) {
                    ...SbSharedRefetchContainer_regionalInfo
                }
                shippingFilters(
                    uriRef: $uriRef
                    zipCode: $userZipCode
                    countryCode: $userCountryCode
                ) @include(if: $hasPersonalizedFilter) {
                    ...SbSharedRefetchContainer_shippingFilters
                    ...SbRespComponents_shippingFilters
                }
            }
        }
    `
);
